import React from 'react';
import { observer } from 'mobx-react';
import { List, Box, Divisor, ListItem, Heading } from 'components';
import { formatCountryRegion, formatPrice } from 'utils/formatStrings';

interface PriceBreakdownProps {
  currency: string;
  productionCosts: number;
  shippingCosts: number;
  shippingDiscount: number;
  miscItemsTotalCost?: number;
  takeRate?: number;
  takeRateAmount?: number;
  totalCost: number;
  vatAmount?: number;
  vatPerCent?: number;
  vatCountry?: string;
}
export const PriceBreakdown = observer(
  ({
    currency,
    shippingDiscount,
    takeRate,
    vatPerCent,
    vatCountry,
    ...props
  }: PriceBreakdownProps) => {
    const productionCosts = props.productionCosts / 100;
    const shippingCosts = props.shippingCosts / 100;
    const miscItemsTotalCost = props.miscItemsTotalCost / 100;

    const shouldRenderTakeRateEntry = !!takeRate;
    const takeRateAmount = props.takeRateAmount / 100 || 0;

    const discountedShipping = shippingCosts * (1 - shippingDiscount / 100);

    const subtotal =
      productionCosts +
      discountedShipping +
      miscItemsTotalCost +
      takeRateAmount;
    const total = props.totalCost ? props.totalCost / 100 : subtotal;

    let shippingCostsStyle = {};
    if (shippingDiscount > 0) {
      shippingCostsStyle = {
        textDecoration: 'line-through',
        textDecorationColor: 'red',
      };
    }
    const shouldRenderVatEntry = !!vatPerCent;
    const vatAmount = props.vatAmount / 100 || 0;

    return (
      <Box>
        <Heading level={2} size='medium'>
          Price Breakdown
        </Heading>
        <Box mt={4}>
          <List sx={{ paddingLeft: 0, width: 400 }}>
            <ListItem
              dataTestId='priceBreakDown-production-costs-end-item'
              endItem={formatPrice(productionCosts, currency)}
            >
              <span data-testid='priceBreakDown-production-costs'>
                Production Costs
              </span>
            </ListItem>
            {shouldRenderTakeRateEntry && (
              <ListItem
                dataTestId='priceBreakDown-production-fee-end-item'
                endItem={formatPrice(takeRateAmount, currency)}
                sx={{ paddingLeft: 15 }}
              >
                <span
                  data-testid='priceBreakDown-production-fee'
                  style={{ color: 'grey' }}
                >
                  {takeRate}% Take Rate
                </span>
              </ListItem>
            )}
            <ListItem
              dataTestId='priceBreakDown-shipping-costs-end-item'
              endItem={
                <span style={shippingCostsStyle}>
                  {formatPrice(shippingCosts, currency)}
                </span>
              }
            >
              <span data-testid='priceBreakDown-shipping-costs'>
                Shipping Costs
              </span>
            </ListItem>
            {shippingDiscount > 0 && (
              <ListItem
                dataTestId='priceBreakDown-shipping-discount-end-item'
                endItem={formatPrice(discountedShipping, currency)}
                sx={{ paddingLeft: 15 }}
              >
                <span
                  data-testid='priceBreakDown-shipping-discount'
                  style={{ color: 'grey' }}
                >
                  {shippingDiscount}% Discount
                </span>
              </ListItem>
            )}
            {miscItemsTotalCost > 0 && (
              <ListItem
                dataTestId='priceBreakDown-miscellaneous-items-end-item'
                endItem={formatPrice(miscItemsTotalCost, currency)}
              >
                <span data-testid='priceBreakDown-miscellaneous-items'>
                  Miscellaneous Items
                </span>
              </ListItem>
            )}
            {/* <ListItem
              dataTestId='priceBreakDown-subtotal-end-item'
              bold
              endItem={formatPrice(subtotal, currency)}
            >
              <span data-testid='priceBreakDown-subtotal'>Subtotal</span>
            </ListItem> */}
            {shouldRenderVatEntry && (
              <ListItem bold endItem={formatPrice(vatAmount, currency)}>
                VAT - {formatCountryRegion(vatCountry)} ({vatPerCent.toFixed(2)}
                %)
              </ListItem>
            )}
            <Divisor />
            <ListItem
              dataTestId='priceBreakDown-total-end-item'
              bold
              endItem={formatPrice(total, currency)}
            >
              <span data-testid='priceBreakDown-total'>Total</span>
            </ListItem>
          </List>
        </Box>
      </Box>
    );
  },
);
